/**
 * Types bibliothèque de templates mockup — alias FE typés sur le codegen.
 *
 * Page `/settings/mockup-templates` permet à Stéphanie de cataloguer ses
 * templates (cadres muraux, scènes lifestyle, flat lays) que Vision Tier 1
 * étiquette ensuite. À la publication (S6+) on matchera œuvre↔template par
 * intersection de tags + distance Lab palette.
 */

import type { components } from './api-generated'

export type MockupTemplateResponse = components['schemas']['MockupTemplateResponse']
export type MockupTemplateSummaryResponse = components['schemas']['MockupTemplateSummaryResponse']
export type MockupTemplateMatchResponse = components['schemas']['MockupTemplateMatchResponse']
export type MockupTemplateUpdateRequest = components['schemas']['MockupTemplateUpdateRequest']
export type MockupTemplateAttachTagRequest = components['schemas']['MockupTemplateAttachTagRequest']
export type MockupTemplateTagResponse = components['schemas']['MockupTemplateTagResponse']
export type PaletteEntry = components['schemas']['PaletteEntry']

export type MockupTemplateKind = components['schemas']['MockupTemplateKind']
export type FrameStyle = components['schemas']['FrameStyle']
export type RoomType = components['schemas']['RoomType']
export type MockupTagSource = components['schemas']['MockupTagSource']
export type MockupSourceFormat = components['schemas']['MockupSourceFormat']
export type CompositeBlendMode = components['schemas']['CompositeBlendMode']
export type MockupRole = components['schemas']['MockupRole']

// ── Lookups FR (slugs EN → labels FR pour l'UI atelier) ─────────────────────

export const MOCKUP_TEMPLATE_KIND_FR: Record<MockupTemplateKind, string> = {
  frame_only: 'Cadre seul',
  lifestyle_room: 'Pièce lifestyle',
  flat_lay: 'Flat lay',
  desk_setup: 'Bureau / atelier',
  gallery_wall: 'Mur galerie',
  hand_held: 'Tenu en main',
}

export const MOCKUP_TEMPLATE_KIND_OPTIONS: MockupTemplateKind[] = [
  'frame_only',
  'lifestyle_room',
  'flat_lay',
  'desk_setup',
  'gallery_wall',
  'hand_held',
]

export const ROOM_TYPE_FR: Record<RoomType, string> = {
  living_room: 'Salon',
  bedroom: 'Chambre',
  office: 'Bureau',
  kitchen: 'Cuisine',
  nursery: 'Chambre enfant',
  studio: 'Atelier / studio',
  dining_room: 'Salle à manger',
  bathroom: 'Salle de bain',
  hallway: 'Couloir',
}

export const ROOM_TYPE_OPTIONS: RoomType[] = [
  'living_room',
  'bedroom',
  'office',
  'kitchen',
  'dining_room',
  'nursery',
  'studio',
  'bathroom',
  'hallway',
]

export const FRAME_STYLE_FR: Record<FrameStyle, string> = {
  thin_black: 'Cadre fin noir',
  thick_black: 'Cadre épais noir',
  thin_white: 'Cadre fin blanc',
  wood_natural: 'Bois naturel',
  wood_dark: 'Bois foncé',
  gold: 'Doré',
  floating: 'Flottant',
  none: 'Sans cadre',
}

export const FRAME_STYLE_OPTIONS: FrameStyle[] = [
  'thin_black',
  'thick_black',
  'thin_white',
  'wood_natural',
  'wood_dark',
  'gold',
  'floating',
  'none',
]

// Rôle commercial du mockup dans la séquence Etsy — affiché en badge
// dans <MockupTemplatePicker>. Source de vérité côté BE
// (`MockupRole` dans `schemas/enums.py` + `templates_service._assign_role`).
// Cf. mémoire `project_mockup_matching_redesign` pour la grammaire.
export const MOCKUP_ROLE_FR: Record<MockupRole, string> = {
  opening: 'Ouverture',
  frame_view: 'Vue produit',
  room_context: 'Mise en contexte',
  scale: 'Échelle',
  paper_detail: 'Détail papier',
}

// ── Mask zone — 4 points TL → TR → BR → BL ──────────────────────────────────

export type Point2D = [number, number]
export type MaskZone = [Point2D, Point2D, Point2D, Point2D]

// ── Payload upload (multipart, pas de schéma OpenAPI dédié) ─────────────────

export interface MockupTemplateCreatePayload {
  file: File
  /**
   * Optionnel : si absent, le BE dérive le nom depuis `file.name` via
   * `_derive_name_from_filename` (underscores/tirets → espaces, stem du
   * fichier). Utilisé par le drag-drop d'import en masse — un appel par
   * fichier sans saisie utilisateur.
   */
  name?: string
  kind: MockupTemplateKind
  slug?: string
  notes?: string
  /**
   * Optionnel pour les uploads PSD : le BE auto-extrait les 4 corners
   * depuis le Smart Object. Requis pour les uploads JPG/PNG/WebP plats.
   *
   * Le `mask_aspect_ratio` est dérivé automatiquement de `mask_zone` côté
   * serveur — il n'est jamais envoyé en input. Plus de `format_compatibility`
   * non plus depuis la migration 012 : la compatibilité format est calculée
   * à partir du ratio.
   */
  mask_zone?: MaskZone
  initial_tag_slugs?: string[]
}

// Extensions acceptées par le wizard. Source de vérité côté FE — doit
// rester aligné sur `image_pipeline.TEMPLATE_UPLOAD_EXTENSIONS` côté BE.
export const TEMPLATE_FLAT_IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp'
// Les .psd ne sont pas reconnus par tous les browsers via mime type, donc
// on autorise par extension explicitement (accept supporte les deux).
export const TEMPLATE_UPLOAD_ACCEPT = `${TEMPLATE_FLAT_IMAGE_ACCEPT},.psd,image/vnd.adobe.photoshop`

export function isPsdFile(file: File): boolean {
  return file.name.toLowerCase().endsWith('.psd')
}

export interface ListTemplatesParams {
  only_active?: boolean
  kind?: MockupTemplateKind
  room_type?: RoomType
  frame_style?: FrameStyle
  format_slug?: string
  q?: string
}

export interface MatchForWorkParams {
  work_id: number
  format_slug?: string
  limit?: number
}

export interface BrowseForWorkParams {
  work_id: number
  format_slug?: string
}
